import { useEffect, useState } from 'react';
import { FiCheck, FiSave } from 'react-icons/fi';
import { fetchAdminProfile, updateAdminProfile } from '../../api/admin';
import styles from './ProfileEditor.module.css';

const emptyTranslation = {
  displayName: '', homeHeadline: '', homeIntro: '', aboutTitle: '', aboutIntro: '',
  paragraph1: '', paragraph2: '', paragraph3: '', authorBio: '',
};

const emptyProfile = {
  id: 1,
  initials: 'YC',
  location: 'Melbourne',
  country: 'Australia',
  email: '',
  translations: { en: { ...emptyTranslation }, zh: { ...emptyTranslation } },
};

const readData = (response) => {
  if (response.data?.code !== 200) throw new Error(response.data?.message || 'Request failed.');
  return response.data.data;
};

const normalize = (profile) => ({
  ...emptyProfile,
  ...profile,
  translations: {
    en: { ...emptyTranslation, ...(profile?.translations?.en || {}) },
    zh: { ...emptyTranslation, ...(profile?.translations?.zh || {}) },
  },
});

const ProfileEditor = () => {
  const [profile, setProfile] = useState(emptyProfile);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    fetchAdminProfile()
      .then((response) => { if (active) setProfile(normalize(readData(response))); })
      .catch((requestError) => { if (active) setError(requestError.message || 'Could not load the profile.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const updateCommon = (event) => {
    const { name, value } = event.target;
    setProfile((current) => ({ ...current, [name]: value }));
    setSaved(false);
  };

  const updateTranslation = (event) => {
    const { name, value } = event.target;
    setProfile((current) => ({
      ...current,
      translations: {
        ...current.translations,
        [language]: { ...current.translations[language], [name]: value },
      },
    }));
    setSaved(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      setProfile(normalize(readData(await updateAdminProfile(profile))));
      setSaved(true);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'Could not save the profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.state}>Loading profile…</div>;
  const content = profile.translations[language];

  return (
    <form onSubmit={submit}>
      <header className={styles.pageHeader}>
        <div><p>SITE PROFILE</p><h1>Personal details</h1><span>Update the identity and About content shown across the public site.</span></div>
        <button type="submit" disabled={saving}><FiSave /> {saving ? 'Saving…' : 'Save profile'}</button>
      </header>

      {saved && <div className={styles.success}><FiCheck /> Profile saved to the database.</div>}
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.grid}>
        <section className={styles.card}>
          <div className={styles.sectionTitle}><span>01</span><div><h2>Shared details</h2><p>Used by both language versions.</p></div></div>
          <div className={styles.fieldGrid}>
            <label><span>Initials</span><input name="initials" value={profile.initials} onChange={updateCommon} maxLength="10" /></label>
            <label><span>Email</span><input name="email" type="email" value={profile.email} onChange={updateCommon} /></label>
            <label><span>City</span><input name="location" value={profile.location} onChange={updateCommon} /></label>
            <label><span>Country</span><input name="country" value={profile.country} onChange={updateCommon} /></label>
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.sectionTitle}><span>02</span><div><h2>Localized content</h2><p>Keep the English and Chinese versions together.</p></div></div>
          <div className={styles.tabs} role="tablist" aria-label="Profile content language">
            <button type="button" className={language === 'en' ? styles.active : ''} onClick={() => setLanguage('en')}>English</button>
            <button type="button" className={language === 'zh' ? styles.active : ''} onClick={() => setLanguage('zh')}>中文</button>
          </div>
          <label><span>Display name</span><input name="displayName" value={content.displayName} onChange={updateTranslation} /></label>
          <label><span>Home headline</span><textarea name="homeHeadline" value={content.homeHeadline} onChange={updateTranslation} rows="2" /></label>
          <label><span>Home introduction</span><textarea name="homeIntro" value={content.homeIntro} onChange={updateTranslation} rows="4" /></label>
          <label><span>About title</span><textarea name="aboutTitle" value={content.aboutTitle} onChange={updateTranslation} rows="2" /></label>
          <label><span>About introduction</span><textarea name="aboutIntro" value={content.aboutIntro} onChange={updateTranslation} rows="4" /></label>
          <label><span>About paragraph 1</span><textarea name="paragraph1" value={content.paragraph1} onChange={updateTranslation} rows="4" /></label>
          <label><span>About paragraph 2</span><textarea name="paragraph2" value={content.paragraph2} onChange={updateTranslation} rows="4" /></label>
          <label><span>About paragraph 3</span><textarea name="paragraph3" value={content.paragraph3} onChange={updateTranslation} rows="4" /></label>
          <label><span>Article author bio</span><textarea name="authorBio" value={content.authorBio} onChange={updateTranslation} rows="4" /></label>
        </section>
      </div>
    </form>
  );
};

export default ProfileEditor;
